import { useState, useEffect } from "react";
import { ArrowLeft, Mail, Lock, Eye, EyeOff, User } from "lucide-react";
import { Link } from "react-router-dom";

function AuthPage({
  onAuth,
  authMode,
  setAuthMode,
  email,
  setEmail,
  password,
  setPassword,
  pseudo,
  setPseudo,
  authError,
  isLoading,
  onBack,
  onNewsletterChange,
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [newsletterAccepted, setNewsletterAccepted] = useState(false);
const [localError, setLocalError] = useState("");

  useEffect(() => {
    setTermsAccepted(false);
    setNewsletterAccepted(false);
    setLocalError("");
  }, [authMode]);

  // Le wrapper (position: fixed) est la seule zone scrollable.
  // On neutralise le scroll du document pendant l'affichage.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    const prev = {
      htmlOverflow: html.style.overflow,
      htmlHeight: html.style.height,
      bodyOverflow: body.style.overflow,
      bodyHeight: body.style.height,
      bodyMinHeight: body.style.minHeight,
    };

    html.style.overflow = "hidden";
    html.style.height = "100%";
    body.style.overflow = "hidden";
    body.style.height = "100%";
    body.style.minHeight = "0";

    return () => {
      html.style.overflow = prev.htmlOverflow;
      html.style.height = prev.htmlHeight;
      body.style.overflow = prev.bodyOverflow;
      body.style.height = prev.bodyHeight;
      body.style.minHeight = prev.bodyMinHeight;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanPseudo = String(pseudo || "").trim();

    if (authMode === "signup" && cleanPseudo.length < 2) {
      setLocalError("Choisissez un pseudo d’au moins 2 caractères.");
      return;
    }

    if (authMode === "signup" && String(password || "").length < 6) {
      setLocalError("Votre mot de passe doit contenir au moins 6 caractères.");
      return;
    }

    setLocalError("");

    try {
      await onAuth(e, { pseudo: cleanPseudo, newsletterAccepted });
    } catch (error) {
      console.error("❌ Erreur authentification:", error);
    }
  };

  const changeAuthMode = (nextMode) => {
    if (nextMode === authMode) return;

    setAuthMode(nextMode);
    setEmail("");
    setPassword("");
    setPseudo?.("");
    setShowPassword(false);
    setLocalError("");
  };

  return (
    <div className="hauth-screen">
      {onBack && (
        <button
          type="button"
          className="hauth-back"
          onClick={onBack}
          aria-label="Retourner à l’accueil"
        >
          <ArrowLeft size={20} strokeWidth={2} />
          <span>Retour</span>
        </button>
      )}

      <div className="hauth-container">
        <div className="hauth-card">
          <div className="hauth-header">
            <h1 className="hauth-title">
              {authMode === "login" ? "Bon retour" : "Créez votre espace"}
            </h1>

            <p className="hauth-tagline">
              {authMode === "login"
                ? "Retrouvez votre espace de calme et reprenez là où vous vous étiez arrêté."
                : "Un espace personnel pour respirer, ralentir et retrouver ce qui vous fait du bien."}
            </p>
          </div>

          <div
            className="hauth-switch"
            role="tablist"
            aria-label="Connexion ou inscription"
          >
            <button
              type="button"
              role="tab"
              aria-selected={authMode === "login"}
              className={`hauth-switch-btn ${
                authMode === "login" ? "is-active" : ""
              }`}
              onClick={() => changeAuthMode("login")}
            >
              Connexion
            </button>

            <button
              type="button"
              role="tab"
              aria-selected={authMode === "signup"}
              className={`hauth-switch-btn ${
                authMode === "signup" ? "is-active" : ""
              }`}
              onClick={() => changeAuthMode("signup")}
            >
              Inscription
            </button>
          </div>

          <div className="hauth-form">
            <div className="hauth-group">
              <label htmlFor="email" className="hauth-label">
                Email
              </label>
              <div className="hauth-field">
                <Mail size={18} className="hauth-icon" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && email && password) handleSubmit(e);
                  }}
                  placeholder="votre@email.com"
                  className="hauth-input"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            {authMode === "signup" && (
              <div className="hauth-group">
                <label htmlFor="pseudo" className="hauth-label">
                  Pseudo
                </label>
                <div className="hauth-field">
                  <User size={18} className="hauth-icon" />
                  <input
                    id="pseudo"
                    type="text"
                    value={pseudo}
                    onChange={(e) => setPseudo(e.target.value)}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        email &&
                        password &&
                        pseudo?.trim()?.length >= 2
                      ) {
                        handleSubmit(e);
                      }
                    }}
                    className="hauth-input"
                    required
                    minLength={2}
                    maxLength={12}
                    autoComplete="nickname"
                  />
                </div>
              </div>
            )}

            <div className="hauth-group">
              <label htmlFor="password" className="hauth-label">
                Mot de passe
              </label>
              <div className="hauth-field">
                <Lock size={18} className="hauth-icon" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setLocalError(""); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && email && password) handleSubmit(e);
                  }}
                  placeholder="••••••••"
                  className="hauth-input hauth-input--pw"
                  required
                  autoComplete={
                    authMode === "login" ? "current-password" : "new-password"
                  }
                />
                <button
                  type="button"
                  className="hauth-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={
                    showPassword
                      ? "Masquer le mot de passe"
                      : "Afficher le mot de passe"
                  }
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {authMode === "signup" && (
                <p className="hauth-hint">6 caractères minimum</p>
              )}   
            </div>

            {authMode === "login" && (
              <div className="hauth-extra">
                <Link to="/forgot-password" className="hauth-forgot">
                  Mot de passe oublié ?
                </Link>
              </div>
            )}

            {authMode === "signup" && (
              <div className="hauth-legal">
                <label className="hauth-check">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="hauth-checkbox"
                    required
                  />
                  <span className="hauth-check-text">
                    J'accepte les{" "}
                    <Link
                      to="/legal/cgv"
                      className="hauth-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Conditions Générales
                    </Link>{" "}
                    et la{" "}
                    <Link
                      to="/legal/politique-confidentialite"
                      className="hauth-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Politique de Confidentialité
                    </Link>{" "}
                    <span className="hauth-star">*</span>
                  </span>
                </label>

                <label className="hauth-check">
                  <input
                    type="checkbox"
                    checked={newsletterAccepted}
                    onChange={(e) => {
                      setNewsletterAccepted(e.target.checked);
                      onNewsletterChange?.(e.target.checked);
                    }}
                    className="hauth-checkbox"
                  />
                  <span className="hauth-check-text">
                    J'accepte de recevoir les actualités et conseils bien-être
                    d'Harmonauria par email{" "}
                    <span className="hauth-optional">(facultatif)</span>
                  </span>
                </label>
              </div>
            )}

            {(localError || authError) && (
              <div className="hauth-error">
                <span>⚠️</span>
                <span>{localError || authError}</span>
              </div>
            )}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={
                isLoading ||
                (authMode === "signup" &&
                  (!termsAccepted || !String(pseudo || "").trim()))
              }
              className="hauth-submit"
            >
              {isLoading ? (
                <>
                  <span className="hauth-spinner" />
                  <span>
                    {authMode === "login" ? "Connexion" : "Inscription"}…
                  </span>
                </>
              ) : (
                <span>
                  {authMode === "login" ? "Se connecter" : "Créer mon espace"}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
/* ============================================================
   Préfixe hauth- : aucune classe n'existe dans theme.css
   ni harmonize.css. Zéro interférence, zéro !important.
   ============================================================ */

.hauth-screen {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 60;

  box-sizing: border-box;
  margin: 0;
  padding: 0;

  display: flex;
  flex-direction: column;
  justify-content: safe center;

background: #ffffff;

  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.hauth-screen *,
.hauth-screen *::before,
.hauth-screen *::after {
  box-sizing: border-box;
}

.hauth-container {
  flex: 0 0 auto;

  width: 100%;
  max-width: 540px;
  margin: 0 auto;

  padding:
    max(78px, calc(env(safe-area-inset-top, 0px) + 74px))
    24px
    max(28px, env(safe-area-inset-bottom, 0px));
}

.hauth-card {
  width: 100%;
  margin: 0;
  padding: 0;
}

/* ---------- BOUTON RETOUR ---------- */
.hauth-back {
  position: absolute;
  z-index: 10;

  top: max(20px, calc(env(safe-area-inset-top, 0px) + 10px));
  left: max(18px, calc(env(safe-area-inset-left, 0px) + 10px));

  min-height: 42px;
  padding: 0 12px;

  display: inline-flex;
  align-items: center;
  gap: 7px;

  border: 0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.58);
  color: #506866;

  font-family: var(--font-body, "DM Sans", sans-serif);
  font-size: 0.86rem;
  font-weight: 750;
  cursor: pointer;

  transition: background 180ms ease, color 180ms ease;
}

.hauth-back:hover {
  background: rgba(255, 255, 255, 0.9);
  color: #203e3e;
}

/* ---------- HEADER ---------- */
.hauth-header {
  width: 100%;
  margin: 0 0 24px;
  text-align: center;
}

.hauth-title {
  margin: 0;
  color: #203e3e;
  font-family: var(--font-display, "Nunito", sans-serif);
  font-size: 2.2rem;
  font-weight: 800;
  line-height: 1.06;
  letter-spacing: -0.045em;
}

.hauth-tagline {
  max-width: 460px;
  margin: 12px auto 0;
  color: #506866;
  font-family: var(--font-body, "DM Sans", sans-serif);
  font-size: 0.95rem;
  font-weight: 500;
  line-height: 1.6;
}

/* ---------- SWITCH ---------- */
.hauth-switch {
  width: 100%;
  margin: 0 0 20px;
  padding: 5px;

  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px;

  border: 1px solid rgba(97, 131, 122, 0.12);
  border-radius: 999px;
  background: rgba(203, 226, 219, 0.24);
}

.hauth-switch-btn {
  width: 100%;
  min-height: 42px;
  padding: 8px 14px;

  display: flex;
  align-items: center;
  justify-content: center;

  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #637673;

  font-family: var(--font-body, "DM Sans", sans-serif);
  font-size: 0.88rem;
  font-weight: 800;
  cursor: pointer;

  transition: color 180ms ease, background 180ms ease;
}

.hauth-switch-btn.is-active {
  color: #203e3e;
  background: rgba(255, 255, 255, 0.94);
  box-shadow:
    0 7px 20px rgba(49, 72, 66, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 1);
}

/* ---------- FORMULAIRE ---------- */
.hauth-form {
  width: 100%;
  display: grid;
  gap: 15px;
}

.hauth-group {
  margin: 0;
}

.hauth-label {
  display: block;
  margin: 0 0 7px 3px;
  color: #405b54;
  font-family: var(--font-body, "DM Sans", sans-serif);
  font-size: 0.82rem;
  font-weight: 800;
}

.hauth-field {
  position: relative;
}

.hauth-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #61837a;
  pointer-events: none;
  z-index: 1;
}

.hauth-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;

  display: flex;
  align-items: center;
  padding: 4px;
  border: none;
  background: none;
  color: #61837a;
  cursor: pointer;
}

.hauth-toggle:hover {
  color: #203e3e;
}

.hauth-input {
  width: 100%;
  min-height: 54px;
  padding: 0 16px 0 44px;

  border: 1px solid rgba(97, 131, 122, 0.17);
  border-radius: 16px;

  background: rgba(255, 255, 255, 0.8);
  color: #203e3e;

  font-family: var(--font-body, "DM Sans", sans-serif);
  font-size: 1rem;

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.94),
    0 5px 18px rgba(49, 72, 66, 0.025);

  transition: border-color 180ms ease, background 180ms ease, box-shadow 180ms ease;
}

.hauth-input--pw {
  padding-right: 44px;
}

.hauth-input::placeholder {
  color: rgba(80, 104, 102, 0.48);
}

.hauth-input:hover {
  border-color: rgba(97, 131, 122, 0.28);
  background: rgba(255, 255, 255, 0.94);
}

.hauth-input:focus {
  outline: none;
  border-color: rgba(97, 131, 122, 0.58);
  background: #ffffff;
  box-shadow:
    0 0 0 4px rgba(118, 165, 167, 0.13),
    0 9px 24px rgba(49, 72, 66, 0.06);
}

.hauth-extra {
  margin-top: -6px;
  display: flex;
  justify-content: flex-end;
}

.hauth-forgot {
  color: #61837a;
  font-family: var(--font-body, "DM Sans", sans-serif);
  font-size: 0.82rem;
  font-weight: 750;
  text-decoration: none;
  cursor: pointer;
}

.hauth-forgot:hover {
  color: #203e3e;
  text-decoration: underline;
  text-underline-offset: 4px;
}

.hauth-hint {
  margin: 7px 3px 0;
  color: rgba(80, 104, 102, 0.62);
  font-family: var(--font-body, "DM Sans", sans-serif);
  font-size: 0.76rem;
  font-weight: 600;
}

/* ---------- CHECKBOXES ---------- */
.hauth-legal {
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  border: 1px solid rgba(97, 131, 122, 0.1);
  border-radius: 18px;
  background: rgba(203, 226, 219, 0.16);
}

.hauth-check {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}

.hauth-checkbox {
  flex-shrink: 0;
  width: 17px;
  height: 17px;
  margin-top: 2px;
  accent-color: #61837a;
  cursor: pointer;
}

.hauth-check-text {
  color: #506866;
  font-family: var(--font-body, "DM Sans", sans-serif);
  font-size: 0.79rem;
  line-height: 1.5;
}

.hauth-link {
  color: #405b54;
  font-weight: 800;
  text-decoration: underline;
  text-decoration-color: rgba(97, 131, 122, 0.34);
  text-underline-offset: 3px;
}

.hauth-optional {
  color: rgba(80, 104, 102, 0.62);
}

.hauth-star {
  color: #a85f55;
  font-weight: 700;
}

/* ---------- ERREUR ---------- */
.hauth-error {
  padding: 12px 14px;
  display: flex;
  align-items: flex-start;
  gap: 9px;

  border: 1px solid rgba(168, 95, 85, 0.16);
  border-radius: 14px;
  background: rgba(168, 95, 85, 0.07);
  color: #874940;

  font-family: var(--font-body, "DM Sans", sans-serif);
  font-size: 0.86rem;
  line-height: 1.5;
}

/* ---------- BOUTON PRINCIPAL ---------- */
.hauth-submit {
  width: 100%;
  min-height: 55px;
  padding: 0 24px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;

  border: 0;
  border-radius: 999px;

  background: linear-gradient(135deg, #61837a 0%, #76a5a7 100%);
  color: #ffffff;

  font-family: var(--font-body, "DM Sans", sans-serif);
  font-size: 0.96rem;
  font-weight: 800;

  box-shadow:
    0 15px 34px rgba(97, 131, 122, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.22);

  cursor: pointer;
  transition: transform 180ms ease, box-shadow 180ms ease, opacity 180ms ease;
}

.hauth-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow:
    0 20px 42px rgba(97, 131, 122, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
}

.hauth-submit:active:not(:disabled) {
  transform: translateY(0);
}

.hauth-submit:disabled {
  opacity: 0.52;
  cursor: not-allowed;
  box-shadow: none;
}

.hauth-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: hauthSpin 0.8s linear infinite;
}

@keyframes hauthSpin {
  to { transform: rotate(360deg); }
}

/* ---------- TABLETTE ---------- */
@media (min-width: 768px) {
  .hauth-container {
    max-width: 720px;
    padding-left: 40px;
    padding-right: 40px;
  }

  .hauth-title { font-size: 2.6rem; }
  .hauth-tagline { max-width: 540px; font-size: 1rem; }
  .hauth-input { min-height: 58px; }
  .hauth-switch-btn { min-height: 46px; font-size: 0.92rem; }
  .hauth-submit { min-height: 58px; font-size: 1rem; }
}

/* ---------- DESKTOP ---------- */
@media (min-width: 1024px) {
  .hauth-container {
    max-width: 880px;
    padding-left: 44px;
    padding-right: 44px;
  }

  .hauth-title { font-size: 3rem; }

  .hauth-tagline {
    max-width: 640px;
    margin-top: 16px;
    font-size: 1.06rem;
  }

  .hauth-header { margin-bottom: 32px; }

  .hauth-switch {
    margin-bottom: 26px;
    padding: 6px;
  }

  .hauth-switch-btn { min-height: 48px; font-size: 0.95rem; }
  .hauth-form { gap: 18px; }
  .hauth-label { font-size: 0.86rem; }

  .hauth-input {
    min-height: 60px;
    border-radius: 18px;
    font-size: 1.02rem;
  }

  .hauth-legal { padding: 20px 22px; }
  .hauth-check-text { font-size: 0.84rem; }
  .hauth-submit { min-height: 62px; font-size: 1.02rem; }
}

@media (min-width: 1440px) {
  .hauth-container { max-width: 980px; }
}

/* ---------- MOBILE ---------- */
@media (max-width: 600px) {
  .hauth-container {
    max-width: 430px;
    padding-left: 18px;
    padding-right: 18px;
    padding-top: max(74px, calc(env(safe-area-inset-top, 0px) + 70px));
    padding-bottom: max(26px, env(safe-area-inset-bottom, 0px));
  }

  .hauth-back span { display: none; }

  .hauth-title { font-size: 2.1rem; }
  .hauth-header { margin-bottom: 20px; }
  .hauth-tagline { margin-top: 10px; font-size: 0.88rem; }
  .hauth-switch { margin-bottom: 18px; }
  .hauth-form { gap: 13px; }
  .hauth-input { min-height: 52px; }
  .hauth-submit { min-height: 54px; }
}

@media (max-width: 600px) and (max-height: 720px) {
  .hauth-title { font-size: 1.85rem; }
  .hauth-header { margin-bottom: 15px; }
  .hauth-tagline { margin-top: 8px; font-size: 0.84rem; line-height: 1.45; }
  .hauth-switch { margin-bottom: 14px; }
  .hauth-form { gap: 11px; }
}

@media (max-width: 600px) and (max-height: 640px) {
  .hauth-title { font-size: 1.65rem; }
  .hauth-tagline { display: none; }
  .hauth-header { margin-bottom: 14px; }
  .hauth-input { min-height: 48px; }
  .hauth-switch-btn { min-height: 38px; }
  .hauth-submit { min-height: 50px; }
}

@media (hover: none) and (pointer: coarse) {
  .hauth-submit:hover:not(:disabled) { transform: none; }
}

@media (prefers-reduced-motion: reduce) {
  .hauth-screen *,
  .hauth-screen *::before,
  .hauth-screen *::after {
    animation: none !important;
    transition: none !important;
  }
}
`}</style>
    </div>
  );
}

export default AuthPage;